import React, { useState } from 'react';

import { Button, TextInput, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import {
    addAliceAgentAppAdmin,
    listAliceAgentAppAdmins,
    removeAliceAgentAppAdmin,
} from 'client/api/services/aliceAgentApps';
import { AliceAgentAppAdmin } from 'client/model/aliceAgentApp';
import * as keyset from 'i18n/AliceAgentAppForm';

import styles from './AliceAgentAppAccessManager.module.scss';

const t = i18n(keyset);

export const AliceAgentAppAccessManager: React.FC = () => {
    const { add: toasterAdd } = useToaster();
    const [guidInput, setGuidInput] = useState('');
    const [selectedGuid, setSelectedGuid] = useState<string>();
    const [loginInput, setLoginInput] = useState('');
    const [admins, setAdmins] = useState<AliceAgentAppAdmin[]>();
    const [isLoading, setIsLoading] = useState(false);
    const [isGranting, setIsGranting] = useState(false);
    const [revokingUserId, setRevokingUserId] = useState<string>();

    const showError = (title: string, name: string) => toasterAdd({ title, theme: 'danger', name });

    const handleOpen = async () => {
        const guid = guidInput.trim();
        if (!guid || isLoading) return;
        setIsLoading(true);
        try {
            const nextAdmins = await listAliceAgentAppAdmins(guid);
            setSelectedGuid(guid);
            setAdmins(nextAdmins);
            setLoginInput('');
        } catch {
            showError(t('access-manager-load-error'), 'LOAD_ALICE_AGENT_APP_ADMINS');
        } finally {
            setIsLoading(false);
        }
    };

    const handleGrant = async () => {
        const login = loginInput.trim();
        if (!selectedGuid || !login || isGranting) return;
        setIsGranting(true);
        try {
            await addAliceAgentAppAdmin(selectedGuid, login);
            setAdmins(await listAliceAgentAppAdmins(selectedGuid));
            setLoginInput('');
        } catch {
            showError(t('access-manager-grant-error'), 'GRANT_ALICE_AGENT_APP_ADMIN');
        } finally {
            setIsGranting(false);
        }
    };

    const handleRevoke = async (admin: AliceAgentAppAdmin) => {
        if (!selectedGuid || revokingUserId) return;
        setRevokingUserId(admin.uid);
        try {
            await removeAliceAgentAppAdmin(selectedGuid, admin.uid);
            setAdmins((current) => current?.filter(({ uid }) => uid !== admin.uid));
        } catch {
            showError(t('access-manager-revoke-error'), 'REVOKE_ALICE_AGENT_APP_ADMIN');
        } finally {
            setRevokingUserId(undefined);
        }
    };

    return (
        <section className={styles.container}>
            <h2 className={styles.title}>{t('access-manager-title')}</h2>
            <div className={styles.controls}>
                <TextInput
                    aria-label={t('access-manager-guid-label')}
                    placeholder={t('access-manager-guid-placeholder')}
                    value={guidInput}
                    onUpdate={setGuidInput}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') void handleOpen();
                    }}
                />
                <Button view="action" onClick={handleOpen} loading={isLoading} disabled={!guidInput.trim()}>
                    {t('access-manager-open')}
                </Button>
            </div>

            {selectedGuid && admins && (
                <div className={styles.selectedApp}>
                    <div className={styles.controls}>
                        <TextInput
                            aria-label={t('access-manager-login-label')}
                            placeholder={t('access-manager-login-placeholder')}
                            value={loginInput}
                            onUpdate={setLoginInput}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') void handleGrant();
                            }}
                        />
                        <Button view="action" onClick={handleGrant} loading={isGranting} disabled={!loginInput.trim()}>
                            {t('access-manager-grant')}
                        </Button>
                    </div>
                    <h3 className={styles.listTitle}>{t('access-manager-list-title')}</h3>
                    {admins.length === 0 ? (
                        <p className={styles.empty}>{t('access-manager-empty')}</p>
                    ) : (
                        <ul className={styles.list}>
                            {admins.map((admin) => (
                                <li key={admin.uid} className={styles.admin}>
                                    <span>{admin.login}</span>
                                    <Button
                                        view="flat-danger"
                                        aria-label={`${t('access-manager-revoke')} ${admin.login}`}
                                        onClick={() => void handleRevoke(admin)}
                                        loading={revokingUserId === admin.uid}
                                        disabled={Boolean(revokingUserId)}
                                    >
                                        {t('access-manager-revoke')}
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </section>
    );
};
